// Template rendering and variant rotation. Browser-safe, no dependencies.
import { countSegments } from './segments';
import type { SegmentInfo } from './types';

/** `{{ key }}` or `{{ key | fallback }}`. Group 1 = key, group 2 = fallback (may be undefined). */
const TOKEN = /\{\{\s*([^{}|]*?)\s*(?:\|([^{}]*?))?\s*\}\}/g;

function normalizeVars(vars: Record<string, string | undefined>): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const [key, value] of Object.entries(vars)) {
    lookup.set(key.trim().toLowerCase(), value ?? '');
  }
  return lookup;
}

/**
 * Replace `{{name}}`-style tokens. Keys are trimmed and matched case-insensitively.
 * `{{name|there}}` uses the fallback when the variable is missing or empty; a missing variable
 * with no fallback renders as an empty string (see `unresolvedTokens` to refuse that up front).
 */
export function renderTemplate(text: string, vars: Record<string, string | undefined>): string {
  const lookup = normalizeVars(vars);
  return text.replace(TOKEN, (_match, key: string, fallback: string | undefined) => {
    const value = lookup.get(key.trim().toLowerCase());
    if (value !== undefined && value !== '') return value;
    return fallback !== undefined ? fallback.trim() : '';
  });
}

/**
 * Tokens in `text` that have neither a (non-empty) value in `vars` nor a fallback, as written
 * (trimmed), distinct, in order of first appearance. Empty array = the template renders fully.
 */
export function unresolvedTokens(text: string, vars: Record<string, string | undefined>): string[] {
  const lookup = normalizeVars(vars);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const m of text.matchAll(TOKEN)) {
    const key = (m[1] ?? '').trim();
    const fallback = m[2];
    const value = lookup.get(key.toLowerCase());
    if ((value === undefined || value === '') && fallback === undefined) {
      const id = key.toLowerCase();
      if (!seen.has(id)) {
        seen.add(id);
        out.push(key);
      }
    }
  }
  return out;
}

/**
 * Index of the least-used variant in the last 24 h (ties go to the lowest index), so a
 * reminder template rotates its wordings and never trips the repetition guard.
 */
export function pickVariant(variants: string[], usage24h: Record<number, number>): number {
  if (variants.length === 0) throw new RangeError('pickVariant: no variants to choose from');
  let best = 0;
  let bestCount = usage24h[0] ?? 0;
  for (let i = 1; i < variants.length; i++) {
    const count = usage24h[i] ?? 0;
    if (count < bestCount) {
      best = i;
      bestCount = count;
    }
  }
  return best;
}

// ---- worst-case length after filling in (review round 1, trap 5) -----------------------------

/**
 * Sample values as long as real ones get: the editor counts placeholders ('{{name|there}}' is 14
 * characters) but the text goes out with a 34-character name and 'Wednesday, September 30'.
 * The company name is the workspace's own when the caller passes it.
 */
/** the same length as the sample name, but with the accents GSM 03.38 has no room for */
export const ACCENTED_WORST_CASE_NAME = 'Mar\u00eda Fernanda Rodr\u00edguez Guti\u00e9rrez';

export const WORST_CASE_VARS: Record<string, string> = {
  name: 'Maria Fernanda Rodriguez Gutierrez',
  date: 'Wednesday, September 30',
  time: '10:00 PM',
  company: 'Coastal Energy Water & Air',
  tech: 'Christopher Alexander',
};

/**
 * Render `text` with the longest values we expect and count it, so the editor can say
 * 'about 161 / 306 · 2 segments after filling in'. `overrides` win (the real company name,
 * the longest contact name in the workspace).
 */
export function renderedWorstCase(
  text: string,
  overrides: Record<string, string | undefined> = {},
): { rendered: string; segments: SegmentInfo; accentedSegments: SegmentInfo } {
  const vars: Record<string, string | undefined> = { ...WORST_CASE_VARS };
  for (const [k, v] of Object.entries(overrides)) if (v !== undefined && v !== '') vars[k] = v;
  // a token with no sample value renders as its own name, so an unknown {{thing}} still counts something
  for (const token of unresolvedTokens(text, vars)) vars[token] = token;
  const rendered = renderTemplate(text, vars);
  // WHY (review round 2): the sample name is all GSM-7, so a wording promised as "1 segment" goes
  // out as 2 UCS-2 segments to every Maria/Jose/Raul on the list (i, a, o, u with an acute are NOT
  // in GSM 03.38; only e-grave, e-acute, a-grave, i-grave, o-grave, u-grave, n-tilde and the
  // umlauts are). Report both, so the office is told before it schedules 50 of them.
  const accented = renderTemplate(text, { ...vars, name: overrides.name ?? ACCENTED_WORST_CASE_NAME });
  return { rendered, segments: countSegments(rendered), accentedSegments: countSegments(accented) };
}
